import React from 'react';
import { Pressable, StyleSheet, Switch, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { Copy } from 'lucide-react-native';
import * as Clipboard from 'expo-clipboard';
import { withCvShareRef } from '@tabbio/utils/analytics/cv-traffic';
import { useTranslation } from 'react-i18next';
import type {
  ProfileContactVisibility,
  ProfilePhotoVisibility,
  ProfilePrivacySettings,
  ProfileVisibility,
} from '@tabbio/types';
import {
  deriveCvVisibilityDefaults,
  normalizeProfilePrivacySettings,
} from '@tabbio/utils/profile-privacy';
import { useTheme } from '../../hooks/useTheme';
import { useHaptics } from '../../hooks/useHaptics';
import { useToast } from '../../hooks/useToast';
import { useProfile, useSaveProfilePrivacy } from '../../hooks/useProfileApi';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { CompanySettingsSections } from './CompanySettingsSections';
import {
  ProfileGroupCard,
  ProfileGroupRow,
  ProfileSectionHeader,
  ProfileSheetScaffold,
} from './ProfileSheetScaffold';

export function ProfileSettingsSheet() {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const toast = useToast();

  const {
    publicUrl,
    routeMode,
    hideBranding,
    setHideBranding,
    sensitiveContent,
    setSensitiveContent,
    showContactChannelsPublicly,
    setShowContactChannelsPublicly,
  } = usePublicProfileBuilderModel();
  const profileQuery = useProfile();
  const savePrivacyMutation = useSaveProfilePrivacy();
  const privacy = normalizeProfilePrivacySettings(profileQuery.data?.privacy);
  const safePublicUrl = typeof publicUrl === 'string' ? publicUrl.trim() : '';

  // Company pages get the company-safe settings surface (PRD §14 P0) — the
  // seeker privacy rows never render for a company. routeMode is fixed for a
  // mounted sheet, so the branch after the hooks above is order-stable.
  if (routeMode === 'company') {
    return <CompanySettingsSections />;
  }

  const savePrivacy = (patch: Partial<ProfilePrivacySettings>) => {
    const next = normalizeProfilePrivacySettings({
      ...privacy,
      ...patch,
      contactVisibility: {
        ...privacy.contactVisibility,
        ...patch.contactVisibility,
      },
    });
    const derivedDefaults = deriveCvVisibilityDefaults(next);
    savePrivacyMutation.mutate({
      ...next,
      publicIndexingAcknowledged:
        next.profileVisibility === 'public' ? true : undefined,
      consentSource: 'settings',
      cvVisibilityDefaults: {
        ...next.cvVisibilityDefaults,
        showEmail: derivedDefaults.showEmail,
        showPhone: derivedDefaults.showPhone,
        showPhoto: derivedDefaults.showPhoto,
      },
    });
  };

  const onCopy = async () => {
    if (!safePublicUrl) {
      return;
    }

    // Tagged so the owner's analytics can count visits from a copied link.
    await Clipboard.setStringAsync(withCvShareRef(safePublicUrl, 'copy'));
    haptics.trigger('light');
    toast.show({ title: t('publicProfile.shareSheet.copied') });
  };

  return (
    <ProfileSheetScaffold
      title={t('publicProfile.settingsSheet.title')}
      subtitle={
        safePublicUrl
          ? t('publicProfile.settingsSheet.subtitle')
          : t('publicProfile.settingsSheet.betaSubtitle', {
              defaultValue:
                'Public profile publishing stays disabled in beta until the beta web rollout is ready.',
            })
      }
      compact
    >
      <ProfileSectionHeader title={t('publicProfile.settingsSheet.profileUrl')} />
      <ProfileGroupCard>
        {safePublicUrl ? (
          <ProfileGroupRow
            label={safePublicUrl.replace(/^https?:\/\//, '')}
            rightElement={(
              <Pressable
                onPress={onCopy}
                style={({ pressed }) => [
                  styles.copyButton,
                  {
                    opacity: pressed ? 0.75 : 1,
                    borderColor: theme.borderSubtle,
                    backgroundColor: theme.background,
                  },
                ]}
              >
                <Copy size={15} color={theme.text} strokeWidth={2.2} />
              </Pressable>
            )}
            isLast
          />
        ) : (
          <ProfileGroupRow
            label={t('publicProfile.shareSheet.unavailable', {
              defaultValue: 'Public profile links are unavailable in this environment.',
            })}
            isLast
          />
        )}
      </ProfileGroupCard>

      <ProfileSectionHeader
        title={t('publicProfile.settingsSheet.privacyTitle', {
          defaultValue: 'Privacy',
        })}
      />
      <ProfileGroupCard>
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.profileVisibility', {
            defaultValue: 'Profile',
          })}
          description={t('publicProfile.settingsSheet.profileVisibilityHint', {
            defaultValue:
              'Public creates a searchable web page that search engines may index. Employers only hides public routes but keeps you discoverable to recruiters.',
          })}
          rightElement={(
            <ChoiceGroup<ProfileVisibility>
              value={privacy.profileVisibility}
              disabled={savePrivacyMutation.isPending}
              options={[
                ['public', t('publicProfile.settingsSheet.visibilityPublic', { defaultValue: 'Public' })],
                ['employers', t('publicProfile.settingsSheet.visibilityEmployers', { defaultValue: 'Employers' })],
                ['private', t('publicProfile.settingsSheet.visibilityPrivate', { defaultValue: 'Private' })],
              ]}
              onChange={(profileVisibility) => savePrivacy({ profileVisibility })}
            />
          )}
        />
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.photoVisibility', {
            defaultValue: 'Photo',
          })}
          description={t('publicProfile.settingsSheet.photoVisibilityHint', {
            defaultValue:
              'Hidden removes the real image URL and uses a neutral placeholder.',
          })}
          rightElement={(
            <ChoiceGroup<ProfilePhotoVisibility>
              value={privacy.photoVisibility}
              disabled={savePrivacyMutation.isPending}
              options={[
                ['public', t('publicProfile.settingsSheet.visibilityPublic', { defaultValue: 'Public' })],
                ['employers', t('publicProfile.settingsSheet.visibilityEmployers', { defaultValue: 'Employers' })],
                ['hidden', t('publicProfile.settingsSheet.visibilityHidden', { defaultValue: 'Hidden' })],
              ]}
              onChange={(photoVisibility) => savePrivacy({ photoVisibility })}
            />
          )}
        />
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.emailVisibility', {
            defaultValue: 'Email',
          })}
          rightElement={(
            <ChoiceGroup<ProfileContactVisibility>
              value={privacy.contactVisibility.email}
              disabled={savePrivacyMutation.isPending}
              options={[
                ['employers', t('publicProfile.settingsSheet.visibilityEmployers', { defaultValue: 'Employers' })],
                ['hidden', t('publicProfile.settingsSheet.visibilityHidden', { defaultValue: 'Hidden' })],
              ]}
              onChange={(email) =>
                savePrivacy({ contactVisibility: { email, phone: privacy.contactVisibility.phone } })
              }
            />
          )}
        />
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.phoneVisibility', {
            defaultValue: 'Phone',
          })}
          rightElement={(
            <ChoiceGroup<ProfileContactVisibility>
              value={privacy.contactVisibility.phone}
              disabled={savePrivacyMutation.isPending}
              options={[
                ['employers', t('publicProfile.settingsSheet.visibilityEmployers', { defaultValue: 'Employers' })],
                ['hidden', t('publicProfile.settingsSheet.visibilityHidden', { defaultValue: 'Hidden' })],
              ]}
              onChange={(phone) =>
                savePrivacy({ contactVisibility: { email: privacy.contactVisibility.email, phone } })
              }
            />
          )}
          isLast
        />
      </ProfileGroupCard>

      <ProfileSectionHeader title={t('settings.preferences')} />
      <ProfileGroupCard>
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.showContactChannels')}
          description={t('publicProfile.settingsSheet.showContactChannelsHint')}
          rightElement={(
            <Switch
              value={showContactChannelsPublicly}
              onValueChange={setShowContactChannelsPublicly}
              trackColor={{ false: theme.surfaceTertiary, true: '#22C55E' }}
              thumbColor={theme.background}
              ios_backgroundColor={theme.surfaceTertiary}
            />
          )}
        />
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.hideBranding')}
          description={t('publicProfile.settingsSheet.hideBrandingHint')}
          rightElement={(
            <Switch
              value={hideBranding}
              onValueChange={setHideBranding}
              trackColor={{ false: theme.surfaceTertiary, true: '#22C55E' }}
              thumbColor={theme.background}
              ios_backgroundColor={theme.surfaceTertiary}
            />
          )}
        />
        <ProfileGroupRow
          label={t('publicProfile.settingsSheet.sensitiveContent')}
          description={t('publicProfile.settingsSheet.sensitiveContentHint')}
          rightElement={(
            <Switch
              value={sensitiveContent}
              onValueChange={setSensitiveContent}
              trackColor={{ false: theme.surfaceTertiary, true: '#22C55E' }}
              thumbColor={theme.background}
              ios_backgroundColor={theme.surfaceTertiary}
            />
          )}
          isLast
        />
      </ProfileGroupCard>
    </ProfileSheetScaffold>
  );
}

function ChoiceGroup<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: Array<[T, string]>;
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  const { theme } = useTheme();

  return (
    <View style={styles.choiceGroup}>
      {options.map(([option, label]) => {
        const selected = value === option;
        return (
          <Pressable
            key={option}
            disabled={disabled || selected}
            onPress={() => onChange(option)}
            style={({ pressed }) => [
              styles.choiceButton,
              {
                opacity: pressed ? 0.75 : disabled ? 0.55 : 1,
                borderColor: selected ? theme.text : theme.borderSubtle,
                backgroundColor: selected ? theme.text : theme.background,
              },
            ]}
          >
            <Text
              style={[
                styles.choiceText,
                { color: selected ? theme.background : theme.text },
              ]}
            >
              {label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  copyButton: {
    width: 30,
    height: 30,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  choiceGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 6,
    maxWidth: 180,
  },
  choiceButton: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  choiceText: {
    fontSize: 11,
    fontWeight: '600',
  },
});
